import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AnalyticsService,
  ModalService,
  SimpleModalComponent,
  SimpleModalConfig,
} from '@frk/eds-components';
import { IUserProfile, ProfileSummary } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LOCATION, WINDOW } from '@ng-web-apis/common';
import { PcsToolService } from './pcs-tool.service';
import { Logger } from '@utils/logger';
import { DOCUMENT } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { StorageService } from '@services/storage.service';
import { PCS_ACCEPT_FLAG, USER_GROUP } from '@utils/app.constants';
import { ServerCookieService } from '@services';
import { InteractiveWidget } from '@marketing/interactive-widgets/interactive-widgets.interface';
import { InteractiveWidgetServiceService } from '@marketing/interactive-widgets/interactive-widget-service.service';
import { LaunchDarklyService } from '@services/launch-darkly.service';

const logger = Logger.getLogger('PCSToolComponent');

export interface PCSToolConfig {
  pcsDomain: string;
  pcsApiKey: string;
  launchPcsUrl: string;
  additionalConfig?: {
    contentType: string;
    interactiveWidget: Array<InteractiveWidget>;
  };
}

@Component({
  selector: 'ft-pcs-tool',
  templateUrl: './pcs-tool.component.html',
})
export class PcsToolComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  public pcsAcceptFlag: boolean;
  public modalConfig: SimpleModalConfig;
  public profile: IUserProfile;
  public isLoggedIn: boolean;
  public content: string;
  public brLabels: any;
  public isFirmAllowed = true;
  public isBypassUser: boolean;

  private oAuthToken: string;
  public isPcsConfigArray = false;
  public pcsConfigArray: PCSToolConfig[];
  public currentPcsElement: PCSToolConfig;

  private unsubscribe$: Subject<void> = new Subject<void>();

  @ViewChild('modal', { static: false }) modal: SimpleModalComponent;
  @Input() readonly pcsConfig?: PCSToolConfig;
  @Input() readonly pcsLabels?: object;
  @Input() readonly patToolVariation?: string;
  @Input() readonly metricsEventKey?: string;

  constructor(
    private modalService: ModalService,
    private profileService: ProfileService,
    private pcsToolService: PcsToolService,
    private renderer2: Renderer2,
    private cookieService: CookieService,
    private serverCookieService: ServerCookieService,
    private changeDetectorRef: ChangeDetectorRef,
    private storageService: StorageService,
    private analyticsService: AnalyticsService,
    private interactiveWidgetServiceService: InteractiveWidgetServiceService,
    @Inject(WINDOW) readonly windowRef: Window,
    @Inject(LOCATION) readonly locationRef: Location,
    @Inject(DOCUMENT) readonly documentRef: Document,
    private launchDarklyService: LaunchDarklyService
  ) {
    super();
    this.serverCookieService
      .getOauthToken$()
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((token: string) => {
        this.oAuthToken = token;
      });
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.pcsConfig && this.pcsLabels) {
      this.populate(this.pcsConfig, this.pcsLabels);
    }
  }

  /**
   * Called from interactive widget entry dynamic component
   * @param config - from bloomreach interactive widget component
   * @param labels Br Labels
   */
  public populate(config: PCSToolConfig, labels: any): void {
    this.pcsConfigArray = Array.isArray(config) ? config : [config];
    let firmNames = [];
    // Overriding pcsConfigArray if pcs tool is called from interactive widget document
    if (
      this.pcsConfigArray?.length === 1 &&
      this.pcsConfigArray[0].additionalConfig?.interactiveWidget.length > 0
    ) {
      this.pcsConfigArray = [
        JSON.parse(
          config.additionalConfig.interactiveWidget[0].interactiveConfig
        ),
      ];
      firmNames = config.additionalConfig.interactiveWidget[0].firmName;
    }

    this.brLabels = labels;
    this.setModalData();

    this.content = this.brLabels?.pcsTool_content;

    this.profileService
      .getUserProfile$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profileData: IUserProfile) => {
        this.profile = profileData;
        // If PCS Flag isn't present then consider it as N
        this.pcsAcceptFlag = profileData?.profileInfo?.pcsAcceptFlag === 'Y';
        this.isLoggedIn = true;
        this.storageService
          .retrieve(USER_GROUP, true)
          .then((userGroup: string) => {
            this.profile.profileInfo.userGroup = userGroup;
            // Set user group from validateSession
          });
        this.isFirmAllowed = this.interactiveWidgetServiceService.getIsFirmAllowed(
          firmNames,
          profileData
        );
        this.isBypassUser = this.profileService.isBypass(true);
        this.changeDetectorRef.detectChanges();
      });
  }

  public showInitialTerms(pcsConfigElement?: PCSToolConfig): void {
    // WDE-4593 - Analytics event.
    this.analyticsService.trackEvent({
      event: 'callout_click',
      detailed_event: 'Callout Clicks',
      event_data: {
        category: '',
        link_id: this.patToolVariation,
        link_url: pcsConfigElement?.launchPcsUrl,
        link_type: 'Standard Link',
        link_text: 'Access Now',
      },
      link_url: pcsConfigElement?.launchPcsUrl,
      name: 'Access Now',
      link_text: 'Access Now',
      type: 'PAT',
    });

    // track the click event in launch darkly
    this.launchDarklyService.trackClick(this.metricsEventKey);

    this.currentPcsElement = pcsConfigElement;
    // UDS-1532 - T&C modal was disabled. Moved to PCS tool. Tool will be opened directly.
    this.openTool();
  }

  private openTool(): void {
    this.getInNewTab(this.currentPcsElement.launchPcsUrl);
  }

  public declineTerms1(): void {
    this.modalService.close('termsModal1');
  }

  public acceptTerms1(): void {
    this.saveToDB();
  }

  /**
   * Save users terms and conditions acceptance
   */
  private saveToDB(): void {
    if (!this.currentPcsElement) {
      this.currentPcsElement = this.pcsConfig || this.pcsConfigArray[0];
    }
    if (
      this.currentPcsElement.pcsDomain &&
      this.currentPcsElement.pcsApiKey &&
      this.profile?.profileInfo.userId
    ) {
      this.pcsToolService
        .savePersomalTermsAcceptance$(
          this.currentPcsElement.pcsDomain,
          this.profile?.profileInfo,
          this.currentPcsElement.pcsApiKey
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          () => {
            this.storeProfileSummary();
            this.openTool();
            this.closeModals();
          },
          () => {
            logger.error('Failed to save user preferences.');
            this.closeModals();
          }
        );
    } else {
      logger.debug('Missing config/profile info');
    }
  }

  private closeModals(): void {
    this.modalService.close('termsModal1');
  }

  private setModalData(): void {
    this.modalConfig = {
      modalId: 'termsModal',
      closeBtnLabel: '',
      title: this.brLabels?.pcsToolModal1Title,
      themeTitle: 'dark',
    };
  }

  /**
   * Fires a post request and opens url in a new tab
   * @param obj - request obj
   * @param url - request url
   */
  private postInNewTab(obj: any, url: string): void {
    this.setFormforNewTab(url, 'POST', obj);
  }

  /**
   * Opens the specified URL in a new tab.
   * @param url - String URL to open in a new tab.
   */
  private getInNewTab(url: string): void {
    this.setFormforNewTab(url);
  }

  /**
   * Sets up a form to submit data in a new tab with the specified URL, method, and object parameters.
   * If the method is 'POST', adds hidden input fields for each key-value pair in the object.
   * Submits the form, opens the URL in a new tab, and removes the form element after submission.
   * @param url - String URL where the form data will be sent for processing.
   * @param method - String HTTP method for form submission (default is 'GET').
   * @param obj - Object containing data to be sent with the form (default is null).
   */
  private setFormforNewTab(
    url: string,
    method = 'GET',
    obj: object = null
  ): void {
    const mapForm: HTMLFormElement = this.renderer2.createElement('form');
    // target _blank to open in a new tab
    this.renderer2.setProperty(mapForm, 'target', '_blank');
    this.renderer2.setProperty(mapForm, 'method', method);
    this.renderer2.setProperty(mapForm, 'action', url);
    if (obj && method === 'POST') {
      Object.keys(obj).forEach((param) => {
        const mapInput = this.renderer2.createElement('input');
        this.renderer2.setProperty(mapInput, 'type', 'hidden');
        this.renderer2.setProperty(mapInput, 'name', param);
        this.renderer2.setAttribute(mapInput, 'value', obj[param]);
        this.renderer2.appendChild(mapForm, mapInput);
      });
    }
    this.renderer2.appendChild(this.documentRef.body, mapForm);
    mapForm.submit();
    // remove the temporary element once form has been submitted
    this.renderer2.removeChild(this.documentRef.body, mapForm);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.renderer2.destroy();
  }
  /**
   * Store Profile Summary when savetoDB has successfully completed
   * so that user who hasn't logged in again,
   * doesn't see T&C modal in the session again.p
   */
  private storeProfileSummary() {
    const summary: ProfileSummary = {
      isLoggedIn: this.profile?.isLoggedIn,
      source: this.profile?.loginSource,
      role: this.profile?.profileInfo.role,
      webExperience: this.profile?.profileInfo.webExperience,
      firm: this.profile?.profileInfo.firm,
      accountsAccess: this.profile?.profileInfo.accountsAccess,
      dashboardUrl: this.profile?.profileInfo.dashboardUrl,
      pcsAcceptFlag: 'Y',
    };
    this.storageService.storeProfileSummary(summary);
    this.pcsAcceptFlag = true;
    this.cookieService.set(PCS_ACCEPT_FLAG, 'Y');
  }
}
